import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { AuthToken } from '@shared/signup/models';
import { LocalStorageService } from '@tablesready/frontend-storage';

export const TOKENS_STORAGE_KEY = 'tokens';
export const ACTIVE_TOKEN_STORAGE_KEY = 'tr-auth-token';

// Every tab shares this login. A caller that changes it in-tab must then call
// TabIdentityService.adopt(), or this tab locks itself on its next request.
@Injectable({providedIn: 'root'})
export class TokenService {
  tokens$: BehaviorSubject<AuthToken[] | null>;
  private readonly tokensKey = TOKENS_STORAGE_KEY;
  private readonly activeTokenKey = ACTIVE_TOKEN_STORAGE_KEY;
  private readonly anonTokenKey = 'tr-anon-token';

  constructor (private readonly localStorageService: LocalStorageService) {
    this.tokens$ = new BehaviorSubject(this.getAll());
  }

  setTokens(tokens: (AuthToken & {lastUpdatedAt?: number})[]): void {
    this.localStorageService.set(this.tokensKey, tokens.map<TokenModel>(token => ({...token, lastUpdatedAt: token.lastUpdatedAt || Date.now()})));
    this.tokens$.next(tokens);
  }

  getAll(): TokenModel[] {
    return this.localStorageService.get(this.tokensKey);
  }

  setActiveByLocationId(locationId: string): void {
    const token = (this.getAll() ?? []).find(tokenData => tokenData.locationId === locationId);
    if (token) {
      this.setActive(token);
    }
  }

  setActive(token: AuthToken): void {
    this.localStorageService.set(this.activeTokenKey, {
      ...token,
      expiresIn: token.expiresIn * 1000 + Date.now(),
    });
  }

  getActive(): TokenModel | null {
    try {
      const token = this.localStorageService.get<TokenModel>(this.activeTokenKey);
      if (!Number.isNaN(token.expiresIn) && token.expiresIn <= Date.now()) {
        // The active (access) token is stale. Drop only the active token and keep the refresh
        // tokens so the refresh flow (e.g. on resume after a freeze) can recover the session
        // instead of being wiped out. A real logout still fully clears via clear().
        this.localStorageService.remove(this.activeTokenKey);

        return null;
      }

      return token;
    } catch (err) {
      return null;
    }
  }

  /**
   * Reads the active token's locationId without the expiry side effect of getActive() (which drops
   * the active token). Used by the refresh flow to re-establish the active token after refreshing.
   */
  getActiveLocationId(): string | undefined {
    try {
      return this.localStorageService.get<TokenModel>(this.activeTokenKey)?.locationId;
    } catch (err) {
      return undefined;
    }
  }

  add(tokens: AuthToken[]): void {
    const all = this.getAll();
    // The store was cleared by a logout (getAll() === null) — don't resurrect a logged-out session
    // by rebuilding it from a stale `tokenCreated` event. Mirror update()'s guard.
    if (all === null) {
      return;
    }

    this.setTokens([...all, ...tokens.map<TokenModel>(token => ({...token, lastUpdatedAt: Date.now()}))]);
    this.tokens$.next(this.getAll());
  }

  removeByLocationIds(ids: string[]): void {
    const all = this.getAll();
    // The store was cleared by a logout (getAll() === null) — nothing to remove, and iterating null
    // would throw. Do not rebuild the store.
    if (all === null) {
      return;
    }

    let tokenToRemove: AuthToken = null;
    let isActiveTokenRemoving = false;
    const tokens: AuthToken[] = [];
    // Non-destructive read: getActive() would DROP an expired-but-recoverable active token and
    // return null — which would skip the active-token reselection below when the revoked location
    // is the active one, dead-ending the session in a spurious logout on the next 401.
    const activeLocationId = this.getActiveLocationId();
    for (const token of all) {
      if (ids.indexOf(token.locationId) > -1) {
        tokenToRemove = token;
        isActiveTokenRemoving = token.locationId === activeLocationId || isActiveTokenRemoving;
        continue;
      }
      tokens.push(token);
    }

    if (!tokenToRemove) {
      return;
    }

    if (isActiveTokenRemoving && tokens.length) {
      const tokenWithLocation = tokens.find(token => token.locationId);
      if (tokenWithLocation) {
        this.setActiveByLocationId(tokenWithLocation.locationId);
      } else {
        this.setActive(tokens[0]);
      }
    }
    this.setTokens(tokens);
  }

  update(token: AuthToken): void {
    const all = this.getAll();
    // The store was cleared by a logout (getAll() === null) — don't resurrect a single token by
    // rebuilding from an empty array, which would leave a partial, logged-out token set.
    if (all === null) {
      return;
    }

    const tokens = all.filter(_token => _token.locationId !== token.locationId);
    this.setTokens([...tokens, token]);
  }

  clear(): void {
    this.localStorageService.remove(this.activeTokenKey, this.tokensKey);
    this.tokens$.next(null);
  }

  getAnonToken(): string {
    try {
      // setAnonToken() persists an absolute `expiresAt` (ms epoch); read the same field. The old
      // code read `expiresIn`, which is never stored, so the staleness check was dead and cached
      // anon tokens never expired.
      const token = this.localStorageService.get<AnonTokenModel>(this.anonTokenKey);
      if (!Number.isNaN(token.expiresAt) && token.expiresAt <= Date.now()) {
        // Token is stale
        this.localStorageService.remove(this.anonTokenKey);

        return null;
      }

      return token.token;
    } catch (err) {
      return null;
    }
  }

  setAnonToken(token: string, expiresIn: number): void {
    const currentDate = Date.now();
    const expiresAt = expiresIn + currentDate;
    this.localStorageService.set(this.anonTokenKey, {token, expiresAt, lastUpdatedAt: currentDate});
  }
}

export interface TokenModel extends AuthToken {
  lastUpdatedAt: number;
}

export interface AnonTokenModel {
  token: string;
  expiresAt: number;
  lastUpdatedAt: number;
}
